import React, { useRef, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

interface MagneticLinkProps {
  href: string;
  label: string;
  /** Small caption rendered under the label */
  meta?: string;
  icon: React.ReactNode;
  /** Draws the animated conic ring + stronger glow for hero links */
  featured?: boolean;
  delay?: number;
}

const SPRING = { stiffness: 260, damping: 22, mass: 0.6 };

/**
 * Link row with a magnetic pull toward the cursor, a cursor-following sheen,
 * a light 3D tilt and a press-down response. All motion is disabled when the
 * user prefers reduced motion — the link stays fully usable.
 */
export const MagneticLink: React.FC<MagneticLinkProps> = ({
  href,
  label,
  meta,
  icon,
  featured = false,
  delay = 0,
}) => {
  const ref = useRef<HTMLAnchorElement>(null);
  const reduced = useReducedMotion();
  const [hovered, setHovered] = useState(false);

  // Normalised cursor offset from the element centre, in the range [-0.5, 0.5]
  const px = useMotionValue(0);
  const py = useMotionValue(0);
  // Raw cursor position in px, used to place the sheen
  const sx = useMotionValue(0);
  const sy = useMotionValue(0);

  const mx = useSpring(px, SPRING);
  const my = useSpring(py, SPRING);

  const translateX = useTransform(mx, (v) => v * 14);
  const translateY = useTransform(my, (v) => v * 10);
  const rotateY = useTransform(mx, (v) => v * 9);
  const rotateX = useTransform(my, (v) => v * -7);
  const iconX = useTransform(mx, (v) => v * 8);

  const handleMove = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (reduced || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    sx.set(x);
    sy.set(y);
    px.set(x / rect.width - 0.5);
    py.set(y / rect.height - 0.5);
  };

  const reset = () => {
    setHovered(false);
    px.set(0);
    py.set(0);
  };

  const sheen = useTransform(
    [sx, sy],
    ([x, y]: number[]) =>
      `radial-gradient(220px circle at ${x}px ${y}px, rgba(248,113,113,0.22), rgba(220,38,38,0.07) 45%, transparent 72%)`
  );

  return (
    <motion.a
      ref={ref}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseMove={handleMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={reset}
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-8% 0px' }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      whileTap={reduced ? undefined : { scale: 0.975 }}
      style={{
        x: translateX,
        y: translateY,
        rotateX,
        rotateY,
        transformPerspective: 900,
      }}
      className="group relative flex h-16 w-full items-center gap-4 overflow-hidden rounded-2xl border border-white/[0.10] bg-white/[0.045] px-5 text-left backdrop-blur-2xl transition-[border-color,box-shadow,background-color] duration-500 hover:border-red-500/45 hover:bg-white/[0.075] hover:shadow-[0_18px_50px_-18px_rgba(220,38,38,0.65)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
    >
      {/* Animated ring for featured links */}
      {featured && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-2xl opacity-60 transition-opacity duration-500 group-hover:opacity-100"
          style={{
            padding: '1px',
            background:
              'conic-gradient(from 0deg, transparent 0deg, rgba(248,113,113,0.85) 70deg, transparent 150deg, transparent 210deg, rgba(220,38,38,0.7) 290deg, transparent 360deg)',
            WebkitMask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
            animation: 'ring-spin 6s linear infinite',
          }}
        />
      )}

      {/* Cursor sheen */}
      <motion.span
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
        style={{ background: sheen }}
      />

      {/* Sweeping light on hover */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 -left-full w-1/2 skew-x-[-18deg] bg-gradient-to-r from-transparent via-white/[0.13] to-transparent transition-none group-hover:animate-sweep"
      />

      {/* Top hairline highlight */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent"
      />

      <span className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-black/40 text-white/80 transition-all duration-300 group-hover:border-red-500/40 group-hover:text-red-400 group-hover:shadow-[0_0_18px_-2px_rgba(239,68,68,0.7)]">
        {icon}
      </span>

      <span className="relative flex min-w-0 flex-col">
        <span className="truncate text-[0.95rem] font-semibold tracking-wide text-white/90 transition-colors duration-300 group-hover:text-white">
          {label}
        </span>
        {meta && (
          <span className="truncate text-xs font-medium tracking-wide text-white/35 transition-colors duration-300 group-hover:text-red-400/80">
            {meta}
          </span>
        )}
      </span>

      <motion.span
        aria-hidden
        style={{ x: iconX }}
        className="relative ml-auto flex items-center text-white/25 transition-colors duration-300 group-hover:text-red-400"
      >
        <ArrowUpRight
          className={`h-5 w-5 transition-transform duration-300 ${
            hovered ? 'translate-x-0.5 -translate-y-0.5' : ''
          }`}
        />
      </motion.span>
    </motion.a>
  );
};

export default MagneticLink;
